/**
 * Service for lazy loading audio transcripts from Firestore
 *
 * When a user sends an audio message, the transcript is generated asynchronously
 * by the backend. This service fetches the transcript on-demand when the user
 * clicks to view it, rather than loading it with the initial message.
 */

import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { getIdentifierConfig } from "@/lib/identifier-config";

export interface TranscriptFetchResult {
  transcript: string | null;
  audioMetadata?: {
    fileName: string;
    downloadUrl?: string; // Optional - generated dynamically from fileName + userId + projectId
    duration: number;
    mime_type: string;
  };
}

/**
 * Fetch transcript for an audio message from Firestore by filename
 *
 * This function retrieves the user message containing the audio transcription
 * by querying for messages that have metadata.audio matching the given filename.
 * Supports both:
 * - metadata.audio.fileName (direct match)
 * - metadata.audio.gcs_path ending with the filename (Firestore format)
 *
 * The filename is a unique identifier generated when the audio is uploaded.
 *
 * @param userId - User ID (Firebase Auth UID)
 * @param conversationId - Conversation/Project ID
 * @param audioFileName - The audio file name (e.g., "1765616254071_audio.webm")
 * @returns Transcript text and audio metadata, or null if not found/ready
 *
 * @example
 * ```typescript
 * const result = await fetchTranscript(userId, conversationId, "1765616254071_audio.webm");
 * if (result.transcript) {
 *   console.log("Transcript:", result.transcript);
 * } else {
 *   console.log("Transcript not yet available");
 * }
 * ```
 */
export async function fetchTranscript(
  userId: string,
  conversationId: string,
  audioFileName: string
): Promise<TranscriptFetchResult> {
  const config = getIdentifierConfig();

  try {
    // Get all user messages in this conversation and find the one with matching audio filename
    // Note: Firestore doesn't support querying nested array fields directly,
    // so we need to fetch messages and filter client-side
    const messagesRef = collection(
      db(),
      "users",
      userId,
      config.collectionName,
      conversationId,
      "messages"
    );

    // Query for user messages only (role === "user")
    const q = query(messagesRef, where("role", "==", "user"));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return { transcript: null };
    }

    // Search through messages to find one with matching audio filename
    for (const messageDoc of querySnapshot.docs) {
      const data = messageDoc.data();
      const content = data.content;

      if (!Array.isArray(content)) continue;

      // Find document block with matching audio filename
      // Support both fileName direct match and gcs_path ending with filename
      const documentBlock = content.find((block: any) => {
        if (block.type !== "document" || block.source?.type !== "text") {
          return false;
        }

        const audioMeta = block.metadata?.audio;
        if (!audioMeta) return false;

        // Check direct fileName match
        if (audioMeta.fileName === audioFileName) {
          return true;
        }

        // Check gcs_path ending with filename (Firestore format: "audio/1765616254071_audio.webm")
        if (audioMeta.gcs_path && audioMeta.gcs_path.endsWith(audioFileName)) {
          return true;
        }

        // Check gcs_path filename extraction
        if (audioMeta.gcs_path) {
          const gcsFileName = audioMeta.gcs_path.split("/").pop();
          if (gcsFileName === audioFileName) {
            return true;
          }
        }

        return false;
      });

      if (documentBlock) {
        return {
          transcript: documentBlock.source.data || null,
          audioMetadata: documentBlock.metadata?.audio,
        };
      }
    }
    return { transcript: null };
  } catch (error) {
    console.error(`[TranscriptService] Failed to fetch transcript for audio ${audioFileName}:`, error);
    throw error;
  }
}
